import { Button } from '@consigli/facade';
import {
  usePackageId,
  useUpdateBatchFindingsMutation,
  useServiceName,
  usePackageByTypeAndPackageId,
} from '@consigli/hooks';
import { FindingStatus, DocumentPackageType } from '@consigli/types';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlPaperPlane } from 'react-icons/sl';
import { toast } from 'react-toastify';

import { MultipleSelectionDialog } from '@/components/multiple-selection-dialog';
import { ServiceIdIndexValue } from '@/routes';
import { useDemoMode } from '@/util/demo-mode';
import { CheckableFinding } from '@/util/types';

import { mapActionNameToStates } from '../findings-content';
import { useFindingsContext } from '../findings-context';
import { AddLabelButton } from './add-label-button';
import { ProcessingPopup } from './processing-popup';

type BatchUpdateButtonProps = {
  projectId: number;
  buttonKeys: string[];
  count: number;
  showButtons: boolean;
  setShowButtons: Dispatch<SetStateAction<boolean>>;
  itemsOnPage: number;
  onComplete: () => void;
};

export const BatchUpdateButton: FC<BatchUpdateButtonProps> = ({
  projectId,
  buttonKeys,
  count,
  showButtons,
  setShowButtons,
  itemsOnPage,
  onComplete,
}) => {
  const { t } = useTranslation();
  const packageId = usePackageId();
  const serviceName = useServiceName();
  const { page, setPage, pageRecords } = useFindingsContext();
  const [updateBatchFindings, updateBatchFindingsResult] = useUpdateBatchFindingsMutation();
  const [isForwardFindingOpen, setForwardFindingOpen] = useState(false);
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );
  const isPublished = pkg ? pkg.published : false;

  const checkStatus = (status?: FindingStatus) => {
    switch (status) {
      case FindingStatus.NOT_RELEVANT:
        return {
          icon: 'table-status-notrelevant',
          text: 'findings.states.notrelevant',
          status: FindingStatus.NOT_RELEVANT,
        };
      case FindingStatus.ACTIVE:
        return {
          icon: 'table-status-unaddressed',
          text: 'findings.states.unaddressed',
          status: FindingStatus.ACTIVE,
        };
      case FindingStatus.PROCESSING:
        return {
          icon: 'table-status-waiting',
          text: 'findings.states.waiting',
          status: FindingStatus.PROCESSING,
        };
      case FindingStatus.PROCESSED:
        return {
          icon: 'table-status-processed',
          text: 'findings.states.processed',
          status: FindingStatus.PROCESSED,
        };
      default:
        return {
          icon: 'table-status-notrelevant',
          text: 'findings.states.__fallback',
          status: FindingStatus.NOT_RELEVANT,
        };
    }
  };

  useEffect(() => {
    if (updateBatchFindingsResult.status === QueryStatus.fulfilled) {
      if (updateBatchFindingsResult.originalArgs == null) {
        throw new Error('Cannot display toast when response.originalArgs is missing!');
      }
      const text = t(checkStatus(updateBatchFindingsResult.originalArgs.data[0].status).text);
      toast.info(t('toast.file-update-success', { status: text }));
    }
  }, [updateBatchFindingsResult, t]);

  useEffect(() => {
    if (updateBatchFindingsResult.isError) {
      toast.error(t('toast.file-update-failure'));
    }
  }, [updateBatchFindingsResult, t]);

  const updateFindings = useCallback(
    async (status: FindingStatus) => {
      if (itemsOnPage === count && page > 1) {
        setPage((prevPage) => prevPage - 1);
      }
      await updateBatchFindings({
        projectId,
        packageId,
        data: pageRecords
          .filter((finding: CheckableFinding) => finding.checked)
          .map((finding) => ({
            id: finding.id,
            status,
          })),
      });
      onComplete();
    },
    [
      updateBatchFindings,
      projectId,
      packageId,
      pageRecords,
      itemsOnPage,
      count,
      page,
      onComplete,
      setPage,
    ],
  );

  // Select only checked findings
  const checkedFindings = useMemo(
    () => pageRecords.filter((finding: CheckableFinding) => finding.checked),
    [pageRecords],
  );

  if (!showButtons) return <></>;

  return (
    <>
      <MultipleSelectionDialog
        dragId="batchUpdateDraggable"
        title={`${count} ${t('findings.findings-selected')}`}
        onClose={() => {
          setShowButtons(false);
          onComplete();
        }}
        leftPosition="30%"
      >
        <div className="flex flex-nowrap gap-2 overflow-x-auto min-w-[500px] pb-1">
          {buttonKeys
            .filter((key: string) => {
              // Hide delete button if admin features are disabled OR if document package is published
              if (
                (!adminFeaturesEnabled || isPublished) &&
                mapActionNameToStates[key].status === FindingStatus.DELETED
              ) {
                return false;
              }
              return true;
            })
            .map((key: string) => (
              <Button
                secondary
                rounded
                key={key}
                icon={
                  mapActionNameToStates[key].status === FindingStatus.PROCESSING
                    ? SlPaperPlane
                    : mapActionNameToStates[key].icon
                }
                onClick={() => {
                  if (mapActionNameToStates[key].status === FindingStatus.PROCESSING) {
                    setForwardFindingOpen(true);
                    return;
                  }
                  updateFindings(mapActionNameToStates[key].status);
                }}
                className="px-2 py-1 bg-day-light-4 border-1 rounded-md h-[30px] justify-start"
              >
                <span className="font-bold text-sm">{t(`findings.status.${key}`)}</span>
              </Button>
            ))}
          <AddLabelButton findings={checkedFindings} onComplete={onComplete} />
        </div>
      </MultipleSelectionDialog>
      {isForwardFindingOpen && (
        <ProcessingPopup
          findings={checkedFindings}
          count={count}
          itemsOnPage={itemsOnPage}
          onClose={() => setForwardFindingOpen(false)}
          onComplete={onComplete}
        />
      )}
    </>
  );
};
